import React, { useState } from 'react'

import Toast, { TOAST_TYPE } from 'components/Toast/Toast'
import Upload from 'components/Forms/Upload/Upload'
import Input from 'components/Forms/Input/Input'
import Toggle from 'components/Forms/Toggle/Toggle'
import Select from 'components/Forms/Select/Select'
import JsonCodeBlock from 'components/Forms/CodeBlock/JSON/JSON'
import FormBlock from 'components/Forms/FormBlock'
import LocaleFormBlock, { CONTROL_TYPES } from 'components/Forms/components/LocaleFormBlock/LocaleFormBlock'
import FormModal from 'components/Modal/FormModal/FormModal'

import {
    fieldsValidator,
    getIsValueRule,
    getIsIntStringRule,
    getIsNotEmptyStringRule,
    getIsJsonStringRule,
} from 'utils/fieldsValidator'
import { encodeOrDecodeJsonStructure, setKeyToObject, removeKeyFromObject } from 'utils/common'
import {
    getLocaleDependentTextFieldsStateObject,
    addLocaleDependentTextFieldsToValidator,
    convertToList,
} from 'utils/translations'

import { getBlocks } from 'constants/blocks'
import { DEFAULT_LOCALE } from 'constants/locales'

const LOCALE_DEPENDENT_FIELDS = {
    NAME: {
        fieldName: 'name',
    },
    DESCRIPTION: {
        fieldName: 'description',
    },
}

const CreateTemplateModal = ({ onSubmit = () => {}, onClose = () => {} }) => {
    const [activeNameLocale, setActiveNameLocale] = useState(DEFAULT_LOCALE)
    const [activeDescriptionLocale, setActiveDescriptionLocale] = useState(DEFAULT_LOCALE)

    const [localeDependentFields, setLocaleDependentFields] = useState(() =>
        getLocaleDependentTextFieldsStateObject(LOCALE_DEPENDENT_FIELDS),
    )

    const [isShowDescription, setIsShowDescription] = useState(true)

    const [blockType, setBlockType] = useState(null)

    const [isEnabled, setIsEnabled] = useState(true)

    const [image, setImage] = useState(null)
    const [imageUrl, setImageUrl] = useState('')

    const [orderNumber, setOrderNumber] = useState('')

    const [structureJson, setStructureJson] = useState('')

    const [errors, setErrors] = useState({})

    const blocks = getBlocks()
    const blockTypesList = blocks.map(el => ({
        value: el.id,
        label: el.name,
    }))

    const handleSubmit = async () => {
        try {
            setErrors({})

            const validator = new fieldsValidator()
            addLocaleDependentTextFieldsToValidator(validator, {
                fields: LOCALE_DEPENDENT_FIELDS,
                values: localeDependentFields,
                excludeFields: {
                    [LOCALE_DEPENDENT_FIELDS.DESCRIPTION.fieldName]: !isShowDescription,
                },
            })
            validator.addFields([
                validator.createField('blockType', blockType, [getIsValueRule()]),
                validator.createField('image', imageUrl, [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(image ? 'File must be uploaded' : 'Field is required'),
                ]),
                validator.createField('orderNumber', String(orderNumber), [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(),
                    getIsIntStringRule(),
                ]),
                validator.createField('structureJson', structureJson, [
                    getIsValueRule(),
                    getIsNotEmptyStringRule(),
                    getIsJsonStringRule(),
                ]),
            ])

            const { isHasErrors, errors } = validator.validate()
            if (isHasErrors) {
                setErrors(errors)
                return
            }

            const nameTransl = localeDependentFields[LOCALE_DEPENDENT_FIELDS.NAME.fieldName]
            const newNameTransl = convertToList(nameTransl)
            const descriptionTransl = localeDependentFields[LOCALE_DEPENDENT_FIELDS.DESCRIPTION.fieldName]
            const newDescriptionTransl = convertToList(descriptionTransl)
            await onSubmit({
                id: null,
                imageUrl,
                structureJson: JSON.stringify(encodeOrDecodeJsonStructure(JSON.parse(structureJson), 'encode')),
                enabled: isEnabled,
                blockTypeId: blockType.value,
                orderNumber: parseInt(orderNumber),
                name: newNameTransl,
                isShowDescription: isShowDescription,
                description: newDescriptionTransl,
            })

            return true
        } catch (err) {
            console.error(err)
            Toast(TOAST_TYPE.ERROR)
            throw err
        }
    }

    return (
        <FormModal
            headText="Create template"
            successText="Template created successfully!"
            actionText="Create"
            onAction={() => handleSubmit()}
            onClose={() => onClose()}
        >
            <LocaleFormBlock
                label="Name"
                errors={errors}
                fields={localeDependentFields}
                fieldName={LOCALE_DEPENDENT_FIELDS.NAME.fieldName}
                activeTabName={activeNameLocale}
                onChangeActiveTabName={v => setActiveNameLocale(v)}
                onChange={data => {
                    setLocaleDependentFields(data.value)
                    setErrors(data.errors)
                }}
            />
            <FormBlock>
                <Toggle
                    value={isShowDescription}
                    label="Show description"
                    onChange={value => {
                        setIsShowDescription(value)
                        setErrors(removeKeyFromObject(errors, 'isShowDescription'))
                    }}
                />
            </FormBlock>
            {isShowDescription && (
                <LocaleFormBlock
                    label="Description"
                    controlType={CONTROL_TYPES.TEXTAREA}
                    errors={errors}
                    fields={localeDependentFields}
                    fieldName={LOCALE_DEPENDENT_FIELDS.DESCRIPTION.fieldName}
                    activeTabName={activeDescriptionLocale}
                    onChangeActiveTabName={v => setActiveDescriptionLocale(v)}
                    onChange={data => {
                        setLocaleDependentFields(data.value)
                        setErrors(data.errors)
                    }}
                />
            )}
            <br />
            <FormBlock>
                <Toggle
                    value={isEnabled}
                    label="Enabled"
                    onChange={value => {
                        setIsEnabled(value)
                        setErrors(removeKeyFromObject(errors, 'isEnabled'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Type">
                <Select
                    error={errors.blockType}
                    isDisabled={false}
                    value={blockType}
                    options={blockTypesList}
                    onChange={value => {
                        setBlockType(value)
                        setErrors(removeKeyFromObject(errors, 'blockType'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Image">
                <Upload
                    value={image}
                    error={errors.image}
                    isUploaded={!!imageUrl}
                    onReset={() => {
                        setImageUrl('')
                        setImage(null)
                    }}
                    onUpload={({ isSuccess, url, errorText }) => {
                        if (!isSuccess) {
                            setErrors(setKeyToObject(errors, 'image', errorText))
                            return
                        }
                        setImageUrl(url)
                        setErrors(removeKeyFromObject(errors, 'image'))
                    }}
                    onChange={file => {
                        setImageUrl('')
                        setImage(file)
                        setErrors(removeKeyFromObject(errors, 'image'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Order number">
                <Input
                    type="number"
                    min={1}
                    value={orderNumber}
                    error={errors.orderNumber}
                    onChange={value => {
                        setOrderNumber(value)
                        setErrors(removeKeyFromObject(errors, 'orderNumber'))
                    }}
                />
            </FormBlock>
            <FormBlock label="Template structure (JSON)">
                <JsonCodeBlock
                    value={structureJson}
                    error={errors.structureJson}
                    onChange={value => {
                        setStructureJson(value)
                        setErrors(removeKeyFromObject(errors, 'structureJson'))
                    }}
                />
            </FormBlock>
        </FormModal>
    )
}

export default CreateTemplateModal
